export const MAX_IMPORT_HISTORY_FILES = 10;

export const IMPORT_BATCH_STATUSES = Object.freeze({
  UPLOADING: "uploading",
  PROCESSING: "processing",
  READY: "ready_for_review",
  PARTIAL_FAILURE: "partial_failure",
  REVIEWED: "reviewed",
  DISMISSED: "dismissed",
});

const TERMINAL_SOURCE_STATUSES = new Set(["needs_review", "organized", "saved_source", "saved_as_original_note", "failed", "error"]);

const FAILED_SOURCE_STATUSES = new Set(["failed", "error"]);

const hasImportHistoryReviewResult = (source = {}) => (
  source.pendingTimelineReview?.useCase === "import_history"
  || source.importHistoryReviewResult?.version > 0
);

export const deriveImportBatchProgress = ({ batch = {}, sources = [] } = {}) => {
  const sourceById = new Map(sources.map((source) => [source.id, source]));
  const items = (batch.items || []).map((item) => ({
    ...item,
    source: item.sourceNoteId ? sourceById.get(item.sourceNoteId) || null : null,
  }));
  const failedCount = items.filter((item) => item.uploadStatus === "failed" || FAILED_SOURCE_STATUSES.has(item.source?.status || item.source?.processingStatus)).length;
  const completedCount = items.filter((item) => item.uploadStatus === "failed" || TERMINAL_SOURCE_STATUSES.has(item.source?.status || item.source?.processingStatus)).length;
  const recoveryItems = items.filter((item) => (
    item.source
    && TERMINAL_SOURCE_STATUSES.has(item.source.status || item.source.processingStatus)
    && !FAILED_SOURCE_STATUSES.has(item.source.status || item.source.processingStatus)
    && !hasImportHistoryReviewResult(item.source)
  ));
  const reviewCount = items.reduce((total, item) => total + Number(item.source?.pendingTimelineReview?.candidateCount || 0), 0);
  const isSettled = items.length > 0 && completedCount === items.length;
  const isComplete = isSettled && recoveryItems.length === 0;

  return {
    items,
    totalCount: items.length,
    completedCount,
    failedCount,
    recoveryCount: recoveryItems.length,
    recoveryItems,
    reviewCount,
    isSettled,
    isComplete,
    status: isComplete ? (failedCount ? IMPORT_BATCH_STATUSES.PARTIAL_FAILURE : IMPORT_BATCH_STATUSES.READY) : IMPORT_BATCH_STATUSES.PROCESSING,
  };
};

const normalizeComparableText = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const tokenSet = (value = "") =>
  new Set(
    normalizeComparableText(value)
      .split(" ")
      .filter((token) => token.length > 2),
  );

const similarity = (left = "", right = "") => {
  const leftTokens = tokenSet(left);
  const rightTokens = tokenSet(right);
  if (!leftTokens.size || !rightTokens.size) return 0;
  const shared = [...leftTokens].filter((token) => rightTokens.has(token)).length;
  return shared / Math.min(leftTokens.size, rightTokens.size);
};

const getCandidateText = (candidate = {}) => candidate.suggestedRecord?.record?.text || candidate.suggestedRecord?.record?.summary || candidate.suggestedRecord?.title || "";

const getCandidateDate = (candidate = {}) => String(candidate.suggestedRecord?.record?.localDate || "");

export const annotatePossibleImportOverlaps = (groups = []) => {
  const orderedGroups = groups.map((group) => ({
    ...group,
    candidates: [...(group.candidates || [])].sort((left, right) => {
      const leftDate = getCandidateDate(left);
      const rightDate = getCandidateDate(right);
      if (!leftDate) return rightDate ? 1 : 0;
      if (!rightDate) return -1;
      return leftDate.localeCompare(rightDate);
    }),
  }));
  const candidates = orderedGroups.flatMap((group) =>
    (group.candidates || []).map((candidate) => ({
      groupId: group.sourceNoteId,
      fileName: group.fileName,
      candidate,
    })),
  );
  const overlapsByKey = new Map();

  candidates.forEach((left, index) => {
    candidates.slice(index + 1).forEach((right) => {
      if (left.groupId === right.groupId) return;
      const leftDate = getCandidateDate(left.candidate);
      const rightDate = getCandidateDate(right.candidate);
      if (!leftDate || leftDate !== rightDate) return;
      if (similarity(getCandidateText(left.candidate), getCandidateText(right.candidate)) < 0.72) return;
      overlapsByKey.set(left.candidate.key, {
        candidateKey: right.candidate.key,
        fileName: right.fileName,
      });
      overlapsByKey.set(right.candidate.key, {
        candidateKey: left.candidate.key,
        fileName: left.fileName,
      });
    });
  });

  return orderedGroups.map((group) => ({
    ...group,
    candidates: (group.candidates || []).map((candidate) => ({
      ...candidate,
      possibleOverlap: overlapsByKey.get(candidate.key) || null,
    })),
  }));
};
